import React, { useState, useEffect, Fragment, useCallback } from "react";
import { useRouteMatch, Link, useHistory } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  ButtonDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
} from "reactstrap";
import Loading from "../../../../components/Loading";
import SiteEdit from "../SiteEdit";

import "./index.css";
import CustomAlert from "../../../../components/CustomAlert";
import StatusMap from "pages/Site/components/StatusMap";
import SiteInformation from "pages/Site/components/SiteInformation";
import SiteProcess from "pages/Site/components/SiteProcess";
import api from "api";
import { Button } from "shared";
import { connect } from "react-redux";
import ModalInput from "components/ModalInput";
import t from "utils/Lang";
import ApiCaller from "api/apiCaller";
import { LISA } from "types";
import _ from "lodash";

type SiteDetailProps = {
  user: any;
};

const SiteDetail = ({ user }: SiteDetailProps) => {
  const history = useHistory();
  const match = useRouteMatch("/sitev2/:site_id");
  const params: any = match?.params;
  const siteId = params?.site_id;
  const [statusChange, setStatusChange] = useState<any>();
  const [openReason, setOpenReason] = useState(false);
  const [users, setUsers] = useState<any>([]);
  const [siteStatusMapping, setSiteStatusMapping] = useState<any>({});
  const [siteData, setSiteData] = useState<{
    data: LISA.SiteApiData | null;
    labels: {[key: string]: string};
    contracts: any;
    customFields: LISA.SiteTemplateApiData[];
  }>({
    data: null,
    labels: {},
    contracts: [],
    customFields: [],
  });
  const [isError, setIsError] = useState(false);

  const [alertData, setAlertData] = useState({
    isShow: false,
    content: "",
    color: "danger",
    errorList: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [dropdownOpen, setOpen] = useState(false);

  const toggle = () => setOpen(!dropdownOpen);
  const getSiteStatusMapping = useCallback(async () => {
    const res = await api.getSiteStatusMapping();
    if (res.statusCode === 200) {
      setSiteStatusMapping(res.result);
    }
  }, []);

  const getUsers = useCallback(async () => {
    const res = await api.getUserListing();
    if (res.result) {
      const normalizedUsers = res.result.map((user: any) => {
        return {
          value: user.id,
          label: `${user.first_name} ${user.last_name}`,
        };
      });
      setUsers(normalizedUsers);
    }
  }, []);

  const getSiteDetail = useCallback(async (doNotGetAttachment?: Boolean) => {
    const [res, customFieldRes, contractRes] = await Promise.all([
      api.getSiteDetail(siteId, doNotGetAttachment),
      ApiCaller.get("studio/site"),
      api.getSiteContract(siteId),
    ]);

    setIsError(res.statusCode >= 400);
    if (res?.result && res.statusCode === 200) {
      setSiteData({
        data: res.result,
        labels: res._labels,
        contracts: contractRes?.result?.contracts || [],
        customFields: _.get(customFieldRes, 'template', []),
      });
    }

    setIsLoading(false);
  }, [siteId]);

  useEffect(() => {
    getUsers();
    getSiteDetail();
    getSiteStatusMapping();
  }, [getSiteDetail, getSiteStatusMapping, getUsers]);

  const changeStatusApi = useCallback(async (apiSuffixToCall?: string, reason?: string) => {
    setSiteData({
      ...siteData,
      ...{ loading: true },
    });
    const res = await api.changeSiteStatus(
      siteId,
      apiSuffixToCall || statusChange,
      reason
    );
    if (res?.result) {
      getSiteDetail(true);
    } else {
      setAlertData({
        ...alertData,
        ...{
          content: res?.message,
          isShow: true,
          errorList: res?.missing_fields,
        },
      });
      setSiteData({
        ...siteData,
        ...{ loading: false },
      });
    }
  }, [alertData, getSiteDetail, siteData, siteId, statusChange]);

  const toggleReason = useCallback(() => setOpenReason(!openReason), [openReason]);
  const onConfirmChangeStatus = useCallback((reason: string) => {
    changeStatusApi(undefined, reason);
    toggleReason();
  }, [changeStatusApi, toggleReason]);

  const changeSiteStatus = useCallback(async (
    apiSuffixToCall: string,
    isRollBack: boolean
  ) => {
    setStatusChange(apiSuffixToCall);
    if (isRollBack) {
      setOpenReason(true);
    } else {
      changeStatusApi(apiSuffixToCall);
    }
  }, [changeStatusApi]);

  const hideSite = useCallback(async () => {
    const res = await api.hideSite(siteId);
    if (res.statusCode === 200) {
      setSiteData((prevState: any) => ({
        ...prevState,
        data: {
          ...prevState.data,
          is_active: false,
        },
      }));

      alert("Đã ẩn mặt bằng");
    }
  }, [siteId]);

  const convertSiteToStore = async () => {
    const confirmationBox = window.confirm(
      "Bạn có chắc sẽ tạo cửa hàng từ mặt bằng này?"
    );
    if (!confirmationBox) return;
    setSiteData({
      ...siteData,
      ...{ loading: true },
    });
    const res = await api.upgradeSiteToStore(siteId);
    if (res?.result) {
      window.location.href = `/store/${res?.result?.id}/edit`;
      setSiteData({
        ...siteData,
        ...{ loading: false },
      });
    } else {
      setAlertData({
        ...alertData,
        ...{
          content: res?.message,
          isShow: true,
          errorList: res?.missing_fields,
        },
      });
      setSiteData({
        ...siteData,
        ...{ loading: false },
      });
    }
  };

  const compare = useCallback((v1: any, v2: any) => {
    if (v1.order_sequence_number > v2.order_sequence_number) return 1;
    if (v1.order_sequence_number < v2.order_sequence_number) return -1;

    return 0;
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return (
      <div style={{padding: 10}}>{t().error_no_permissions}</div>
    );
  }

  if (_.isEmpty(siteData.data) || siteData.data === null) {
    return (
      <div style={{padding: 10}}>{t().error_something_went_wrong}</div>
    );
  }

  return (
    <Container>
      <CustomAlert
        {...{
          content: alertData.content,
          isShow: alertData.isShow,
          color: alertData.color,
          errorList: alertData.errorList,
        }}
      />
      <Row className="store-breadcrumb-view">
        <Col xs={12}>
          <div className="site-detail__button-container">
            <ButtonDropdown isOpen={dropdownOpen} toggle={toggle}>
              <DropdownToggle outline caret>
                {t().action__other_feature}
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem
                  tag={Link}
                  to={`/sitev2/${siteData.data?.id || siteId}/traffic/count`}
                >
                  {t().action__traffic}
                </DropdownItem>
                <DropdownItem
                  tag={Link}
                  to={`/sitev2/${siteData.data?.id || siteId}/comparison`}
                >
                  {t().action__compare}
                </DropdownItem>
                {siteData.data.permissions?.includes("can_hide_site") && (
                  <DropdownItem onClick={hideSite}>
                    {t().action__hide}
                  </DropdownItem>
                )}
                {siteData.data.permissions?.includes(
                  "can_upgrade_site_to_store"
                ) &&
                  _.has(siteData, 'data.store') === false && (
                    <DropdownItem onClick={convertSiteToStore}>
                      {t().action__convert_to_store}
                    </DropdownItem>
                  )}
              </DropdownMenu>
            </ButtonDropdown>
            {siteData.data.status?.is_site_can_edit && (
              <Button
                label={t().action__edit}
                outline
                type="primary"
                customClassName="site-detail__button"
                onClick={() =>
                  history.push(`/sitev2/${siteData.data?.id || siteId}/edit`)
                }
              />
            )}{" "}
            {siteData.data.status?.code && siteStatusMapping[siteData.data.status?.code] &&
              siteStatusMapping[siteData.data.status?.code]
                .sort(compare)
                .map((btn: any) => (
                  <Button
                    key={btn.code}
                    type={btn.button_background_color || "primary"}
                    label={btn.button_text || btn.name}
                    positionIcon="right"
                    onClick={() => {
                      changeSiteStatus(
                        btn.code,
                        btn.order_sequence_number <
                          siteData.data?.status?.order_sequence_number!
                      );
                    }}
                    icon={btn.button_icon || "arrow_forward_ios"}
                    customClassName="site-detail__button"
                  />
                ))}
          </div>
        </Col>
      </Row>
      {siteData.data.status?.code !== "temporarily_closed" &&
      siteData.data.status?.code !== "permanently_closed" ? (
        <Row>
          <Col xs={12}>
            <StatusMap currentStatus={siteData.data?.status} />
          </Col>
        </Row>
      ) : (
        <p style={styles.storeClosedInfo}>{siteData.data.status?.name}</p>
      )}
      <SiteInformation
        address={siteData.data.full_address}
        createdAt={siteData.data.created_on_formatted}
        creator={`${siteData.data.created_by.first_name} ${siteData.data.created_by.last_name}`}
        siteName={siteData.data.name}
        currentStatus={siteData.data.status?.name ?? ''}
        formatSite={siteData.data.store_format?.name ?? ''}
        typeSite={siteData.data.store_type?.name}
        dataStore={siteData.data.store ?? null}
        siteId={siteId}
        assignees={siteData.data.assignees}
        followers={siteData.data.followers}
        users={users}
      />
      <SiteProcess
        statusHistories={siteData.data.status_history}
        currentStep={siteData.data.status?.order_sequence_number ?? 0}
      />
      <SiteEdit
        {...{
          siteDataProps: siteData,
          siteIdProps: siteId,
        }}
      />
      <ModalInput
        isOpen={openReason}
        onConfirm={onConfirmChangeStatus}
        title={t().site_listing__reverse_reason}
        toggle={toggleReason}
        placeholder={t().site_listing__reverse_placeholder}
      />
    </Container>
  );
};

const styles = {
  actionButtonIcon: {
    marginLeft: 6,
  },
  storeClosedInfo: {
    padding: 12,
    color: "var(--color-white)",
    backgroundColor: "var(--danger)",
  },
};

const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
  };
};

export default connect(mapStateToProps)(SiteDetail);
